import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { useShowToast } from '@/design_system/Toast';
import Paragraph from '@/design_system/Typography/Paragraph';
import { type EntityGroupDetail, useUpdateEntityGroup } from '@/models/entityGroup';
import { DeleteButtonIconOnly } from '@/routes/SettingsBeta/components/DeleteButtonIconOnly';

type Props = {
  entity: { id: string; name: string };
  entityGroupId: string;
  allEntities: NonNullable<EntityGroupDetail['entities']>;
};

export const RemoveEntityFromGroupButton = ({ entity, entityGroupId, allEntities }: Props) => {
  const { t } = useLingui();
  const showToast = useShowToast();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutateAsync: updateEntityGroup, isPending: isUpdateEntityGroupPending } =
    useUpdateEntityGroup();

  const handleConfirmRemove = async () => {
    const entityName = entity.name;

    try {
      await updateEntityGroup({
        entityGroupId,
        body: {
          entitiesIds: allEntities.filter((e) => e.id !== entity.id).map((e) => e.id),
        },
      });
      showToast({
        text: (
          <Trans id="settings.organization.entities.groups.detail.remove.success">
            &quot;{entityName}&quot; was removed from the group.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.entities.groups.detail.remove.error">
            Could not remove &quot;{entityName}&quot; from the group. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Update failed');
    }
  };

  return (
    <>
      <DeleteButtonIconOnly
        ariaLabel={t({
          id: 'settings.organization.entities.groups.detail.remove.aria',
          message: `Remove ${entity.name} from group`,
        })}
        onClick={() => {
          setShowConfirmation(true);
        }}
      />

      <ConfirmationDialog
        title={
          <Trans id="settings.organization.entities.groups.detail.remove.title">
            Remove entity from group
          </Trans>
        }
        confirm={
          <Trans id="settings.organization.entities.groups.detail.remove.confirm-button">
            Remove
          </Trans>
        }
        showDialog={showConfirmation}
        setShowDialog={setShowConfirmation}
        isPending={isUpdateEntityGroupPending}
        onConfirm={handleConfirmRemove}
        maxWidth="35rem"
      >
        <Paragraph>
          <Trans id="settings.organization.entities.groups.detail.remove.description">
            Are you sure you want to remove &quot;{entity.name}&quot; from this group?
          </Trans>
        </Paragraph>
      </ConfirmationDialog>
    </>
  );
};
